import { useState, useEffect, useContext } from "react";
import "../Scss/CaseStyles.scss?v=1.1.4";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  Grid,
  LinearProgress,
  Skeleton,
  TextField,
} from "@mui/material/";

import {
  CommonSubHeader,
  formatToTenDigits,
  quitarTildes,
  b64DecodeUnicode,
  hasValidContractConfig,
} from "../../Components/helpers/Helpers";

// SECTIONS
// SECTION N|1
import InfoSection from "../ViewSections/InfoSection";
// SECTION N|2
import ConfigControlSection from "../ViewSections/ConfigControlSection";
// SECTION N|3
import TabsSection from "../ViewSections/TabsSection";
// SECTION N|4 ACORDION
import FlowSectionAccordion from "../ViewSections/FlowSectionAccordion";
import api from "../../api/axios";
import { useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import { PermissionContext } from "../../Components/Context/Context";
import Unauthorized from "../../Components/Common/Design/Unauthorized";

const sanitizeCaseTypeLabel = (value) => {
  const text = (value ?? "").toString().trim();
  if (!text) return "";

  const openCount = (text.match(/\(/g) || []).length;
  const closeCount = (text.match(/\)/g) || []).length;

  // Solo limpia si hay cierre sobrante al final.
  if (closeCount > openCount && /\)\s*$/.test(text)) {
    return text.replace(/\s*\)+\s*$/, "").trim();
  }

  return text;
};

const normalizeDocument = (value) =>
  (value ?? "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const dashboardPalette = {
  primary: "#6366f1",
  primarySoft: "#8b5cf6",

  success: "#4ade80",
  successSoft: "#22c55e",

  warning: "#fbbf24",
  warningSoft: "#f59e0b",

  danger: "#f87171",
  dangerSoft: "#ef4444",

  info: "#60a5fa45",

  textMain: "#e5e7eb",
  textSoft: "#9ca3af",

  same: "rgba(31, 41, 55, 0.8)",

  surface: "rgba(255,255,255,0.04)",
  surfaceBorder: "rgba(255,255,255,0.08)",
};

const glassSurface = {
  width: "100%",
  // mb: "12px",
  borderRadius: "10px",
  // background: dashboardPalette?.surface,
  background: dashboardPalette?.surface,
  backdropFilter: "blur(10px)",
  border: `1px solid ${dashboardPalette?.surfaceBorder}`,
  // backgroundColor: "rgba(31,41,55,0.8)",
  boxShadow: "0 10px 26px rgba(15, 23, 42, 0.07)",
};

const generalStyles = {
  background: "rgba(255, 255, 255, 0.58)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  borderRadius: "12px",
  border: "1px solid rgba(255, 255, 255, 0.5)",
  overflow: "hidden",
};

const ViewCase = () => {
  const Swal = require("sweetalert2");

  // Flag manual para logs de respuestas API.
  const ENABLE_QUERY_LOGS = false;
  const logQueryResponse = (label, payload) => {
    if (!ENABLE_QUERY_LOGS) return;
    console.log(`[ViewCase] ${label}`, payload);
  };

  const [searchParams] = useSearchParams();
  const caseId = searchParams.get("id");
  const { can } = useContext(PermissionContext);
  const hasPermission = can("cases", "view");

  const [progress, setProgress] = useState(10);
  const [flag, setFlag] = useState(false);
  const [invalidContract, setInvalidContract] = useState(false);
  const [userLogged, setUserLogged] = useState(false);

  const [updatedCase, setUpdatedCase] = useState(false);
  const [lawyersList, setLawyersList] = useState([]);
  const [contractsList, setContractsList] = useState([]);
  const [newContract, setNewContract] = useState(null);
  const [contractInputValue, setContractInputValue] = useState("");
  const [requireContractAssignment, setRequireContractAssignment] =
    useState(false);

  // DATA CASE
  const [dataCase, setDataCase] = useState({
    peritaje: 0,
    apud_acta: 0,
    expiration: 0,
  });

  const [caseContract, setCaseContract] = useState({});
  const [acceptedContract, setAcceptedContract] = useState("");

  const [lawyerData, setLawyerData] = useState({
    email: "",
    name: "",
    province: "",
    address: "",
    postalCode: "",
    document: "",
    phone: "",
  });

  const [clientData, setClientData] = useState({
    email: "",
    name: "",
    province: "",
    address: "",
    postalcode: "",
    document: "",
    phone: "",
  });
  // DATA CASE

  // CONTACT CONDITIONS
  const [contactConditionIfExist, setContactConditionIfExist] = useState("");
  const [mailTemplates, setMailTemplates] = useState(null);

  const getContractList = async () => {
    try {
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/cases/create`,
      );

      const list = Array.isArray(response?.data?.contractList)
        ? response.data.contractList
        : [];

      setContractsList(list);
      return list;
    } catch (error) {
      console.error("[ViewCase] Error cargando contratos:", error);
      setContractsList([]);
      return [];
    }
  };

  const handleConfirmNewContract = async () => {
    if (!newContract) return;

    try {
      Swal.fire({
        title: "Aplicando cambios",
        text: "Por favor espere mientras se asigna el contrato al expediente.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      await api.post(`${process.env.REACT_APP_API_URL}/cases/update`, {
        id: Number(caseId || dataCase?.id || 0),
        case_contract: Number(newContract?.id || 0),
      });

      Swal.close();

      await Swal.fire({
        title: "Contrato actualizado",
        text: "El contrato ha sido asignado correctamente.",
        icon: "success",
        confirmButtonColor: "#1ab394",
      });

      setRequireContractAssignment(false);
      setNewContract(null);
      setContractInputValue("");
      setUpdatedCase((prev) => !prev);
    } catch (error) {
      Swal.close();

      Swal.fire({
        title: "Error",
        text: "No se pudo asignar el contrato. Inténtalo nuevamente.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    }
  };

  // PRINCIPAL INFO
  const getDataCase = async () => {
    // setFlag(false);
    const url = `${process.env.REACT_APP_API_URL}/cases/show`;

    try {
      // Reinicia el estado antes de cargar el expediente.
      setInvalidContract(false);
      setAcceptedContract("");
      setNewContract(null);
      setContractInputValue("");
      setRequireContractAssignment(false);

      const response = await api.post(url, { id: caseId });

      console.log(response);

      const caseData = response?.data?.case?.[0] || {};
      const currentCaseTasks = response?.data?.case_tasks || [];
      const hasSavedContractConfig = currentCaseTasks.some((task) =>
        hasValidContractConfig(task?.config),
      );

      const hasCaseContractRelation = Boolean(
        Number(caseData?.case_contract_relation?.id || 0) > 0 ||
        Number(caseData?.case_contract || 0) > 0,
      );

      const availableContracts = await getContractList();
      const shouldRequireContractAssignment =
        !hasCaseContractRelation &&
        !hasSavedContractConfig &&
        availableContracts.length > 0;

      setRequireContractAssignment(shouldRequireContractAssignment);
      if (shouldRequireContractAssignment) {
        setNewContract(null);
      }

      const {
        //  Identificadores Principales
        id,
        case_type_name,
        //  Relaciones con Usuarios y Empresas
        company_name,
        clients_relation,
        //  Datos del Caso
        case_contract_relation,
        case_asociation_name,
        cases_flow_relation,
        last_task,
        last_task_by_pending,
        previous_case_statuses_relation,
        cases_notes_relation,
        cases_alerts_relation,
        peritaje,
        apud_acta,
        expiration,
        //  Información del Flujo del Caso
        full_client_name,
        full_lawyer_name,
        full_office_name,
        //  Relación con Profesionales
        lawyers_relation,
        company_relation,
        types_relation,
        //  Fechas y Actualizaciones
        trial_date,
        last_update,
        // Otros Datos
        inss_users,
        transfer_id,
      } = response.data.case[0];

      logQueryResponse("cases/show", response);

      const formattedNumber = formatToTenDigits(id);

      setLawyersList(response?.data?.lawyersList);

      if (clients_relation?.id) {
        const url = `${process.env.REACT_APP_API_URL}/contacts/search`;

        const formData = new FormData();
        formData.append(
          "document",
          clients_relation?.nif ? clients_relation?.nif : "",
        );
        formData.append(
          "email",
          clients_relation?.email ? clients_relation?.email : "",
        );

        formData.append(
          "company_id",
          clients_relation?.company_id ? clients_relation?.company_id : "",
        );

        const responseSearch = await api.post(url, formData);

        logQueryResponse("contacts/search", responseSearch);

        const contacts = responseSearch?.data?.contacts ?? [];

        if (contacts.length > 0) {
          const clientDocument = normalizeDocument(clients_relation?.nif);

          const documentMatchedContacts = clientDocument
            ? contacts.filter((contact) => {
                const contactDocument = normalizeDocument(
                  contact?.nif ?? contact?.document,
                );
                return contactDocument === clientDocument;
              })
            : [];

          if (documentMatchedContacts.length > 1) {
            setContactConditionIfExist(
              "Existen multiples contactos con el mismo documento. Por favor dejar un unico contacto ligado al cliente.",
            );
          } else if (documentMatchedContacts.length === 1) {
            const conditionText =
              documentMatchedContacts[0]?.condiciones?.trim() ||
              "Sin condiciones asignadas";
            setContactConditionIfExist(conditionText);
          } else {
            setContactConditionIfExist("");
          }
        }
      }

      let totalTasksProgress = 0;
      let signCurrentStatus = null;
      let savedContractHtml = "";

      if (response?.data?.case_tasks?.length > 0) {
        response.data.case_tasks.forEach((element) => {
          if (
            element?.config &&
            typeof element.config === "object" &&
            !Array.isArray(element.config) &&
            "firma" in element.config
          ) {
            signCurrentStatus = element.config;

            try {
              const firma = element.config.firma;
              const decodedFirma = b64DecodeUnicode(firma);

              if (decodedFirma) {
                savedContractHtml = decodedFirma;
              }
            } catch (error) {
              console.error(
                "[ViewCase] Error decodificando firma del expediente:",
                error,
                element,
              );

              setInvalidContract(true);
            }
          }

          if (element?.status === 1) {
            totalTasksProgress++;
          }
        });

        if (savedContractHtml) {
          setAcceptedContract(savedContractHtml);
        }
      }

      // Se calcula el progreso con las tasks completadas vs el numero total de tasks del expediente con una regla de 3.
      if (previous_case_statuses_relation?.length >= totalTasksProgress) {
        const previous = previous_case_statuses_relation?.length ?? 0;
        const realTaskNumber = totalTasksProgress ?? 0;
        const porcentaje = previous > 0 ? (realTaskNumber * 100) / previous : 0;

        setProgress(Math.round(porcentaje));
      }

      setMailTemplates(response.data.company_mail_templates);

      setUserLogged(response?.data?.user_logged);

      const sortedStatuses = [...previous_case_statuses_relation].sort(
        (a, b) => a.order - b.order,
      );

      const oldFlowStatus = [];

      const taskName = quitarTildes(
        cases_flow_relation?.status_name?.toLowerCase(),
      );
      const exceptions = ["firma", "revision"];
      const isException =
        exceptions?.includes(taskName) || !taskName?.includes("finalizado");

      sortedStatuses?.forEach((element) => {
        if (element.order <= cases_flow_relation.order || isException) {
          response?.data?.case_tasks?.forEach((task) => {
            if (task.task_id == element.id) {
              oldFlowStatus.push(element);
            }
          });
        }
      });

      if (!isException) {
        oldFlowStatus.shift();
      }

      oldFlowStatus.unshift({ id: 0, status_name: "Seleccionar" });

      const count = sortedStatuses?.length - 1;
      let penultimate = sortedStatuses[count - 1];

      const flowOrderMap = new Map(
        (sortedStatuses || []).map((flow) => [
          Number(flow?.id),
          Number(flow?.order || 0),
        ]),
      );

      const caseTasks = [...(response?.data?.case_tasks || [])];
      const latestTask =
        [...caseTasks].sort((a, b) => {
          const aOrder = Number(flowOrderMap.get(Number(a?.task_id)) ?? 0);
          const bOrder = Number(flowOrderMap.get(Number(b?.task_id)) ?? 0);

          if (bOrder !== aOrder) {
            return bOrder - aOrder;
          }

          const aDate = a?.date_update || a?.date_in || a?.date_end || "";
          const bDate = b?.date_update || b?.date_in || b?.date_end || "";

          const aTime = aDate ? new Date(aDate).getTime() : 0;
          const bTime = bDate ? new Date(bDate).getTime() : 0;

          return bTime - aTime;
        })[0] || last_task;

      setDataCase({
        id: id,
        case_type_name: sanitizeCaseTypeLabel(case_type_name),
        case_id: formattedNumber,
        alerts: cases_alerts_relation,
        notes: cases_notes_relation,
        peritaje,
        apud_acta,
        expiration,
        task: latestTask,
        latest_task: latestTask,
        penultimateTask: penultimate,
        tasks: response.data.case_tasks,
        completeFlow: sortedStatuses,
        flow: cases_flow_relation,
        last_update,
        old_states: oldFlowStatus,
        transfer_id,
        trial_date,
        clients_relation,
        full_client_name,
        full_lawyer_name,
        full_office_name,
        lawyers_relation,
        company_relation,
        company_name,
      });
      console.log(lawyers_relation);
      setLawyerData({
        id: lawyers_relation.id,
        name: full_lawyer_name,
        office: full_office_name,
        company: company_name,
        association: case_asociation_name,
      });

      setClientData({
        id: clients_relation?.id,
        name: full_client_name,
        document: clients_relation?.nif,
        phone: clients_relation?.phone,
        email: clients_relation?.email,
        address: clients_relation?.address,
        province: clients_relation?.province,
        postalcode: clients_relation?.postalcode,
      });

      const fallbackContractHtml = savedContractHtml || "";
      const contractTemplate = case_contract_relation?.contract || "";
      const text = contractTemplate || fallbackContractHtml;
      const htmlText = String(text || "").replace(/\n/g, "<br>");

      const tagValues = {
        "#NOMBRE#": `<b>${clients_relation?.name}</b>`,
        "#APELLIDOS#": `<b>${clients_relation?.surname}</b>`,
        "#DIRECCION#": `<b>${clients_relation?.address}</b>`,
        "#PROVINCIA#": `<b>${clients_relation?.province}</b>`,
        "#DNI#": `<b>${clients_relation?.nif}</b>`,
      };

      const resultReplaceTags = htmlText.replace(
        /#(\w+)#/g,
        (_, key) => tagValues[`#${key}#`] || key,
      );

      setCaseContract({
        firmaUsuario: signCurrentStatus,
        contract: savedContractHtml || resultReplaceTags || "",
        contractTitle:
          case_contract_relation?.contract_title ||
          (savedContractHtml ? "Contrato guardado" : "Contrato"),
        contractAcceptData: {
          client_name: full_client_name,
          client_nif: clients_relation?.nif || "",
          company_name:
            case_contract_relation?.company_relation?.company_name ||
            company_name ||
            "",
          client_ip: response?.data?.ip ? response.data.ip : "0.0.0.0",
        },
      });
    } catch (error) {
      console.error("Error fetching user data:", error); // Mensaje de error más descriptivo
    } finally {
      setFlag(true); // Asegurarse de desactivar la bandera en caso de error o éxito
      Swal.close();
    }
  };
  // PRINCIPAL INFO

  // ======================================================
  // COMPONENTE INTERNO: LinearProgress con etiqueta
  // ======================================================

  const LinearProgressWithLabel = (props) => {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <b className="infoStyle" style={{ marginRight: "10px" }}>
          Progreso:
        </b>
        <Box sx={{ width: "100%", mr: 1 }}>
          <LinearProgress color="success" variant="determinate" {...props} />
        </Box>
        {props.value}%
      </Box>
    );
  };

  LinearProgressWithLabel.propTypes = {
    value: PropTypes.number.isRequired,
  };

  useEffect(() => {
    getDataCase();
  }, [updatedCase]);

  const content = (
    <>
      <Fade timeout={1000} in={flag}>
        <Box sx={{ marginX: "10px" }}>
          <Box sx={{ mb: "10px" }}>
            <InfoSection
              lawyerData={lawyerData}
              clientData={clientData}
              dataCase={dataCase}
              isCaseLoaded={flag}
              contactConditionIfExist={contactConditionIfExist}
              lawyersList={lawyersList}
              setUpdatedCase={setUpdatedCase}
              updatedCase={updatedCase}
              glassSurface={glassSurface}
            />
          </Box>
          <Box sx={{ mb: "10px" }}>
            <ConfigControlSection
              dataCase={dataCase}
              caseId={caseId}
              setDataCase={setDataCase}
              progress={progress}
              // onFinish={() => console.log("terminado!")}
              setUpdatedCase={setUpdatedCase}
              updatedCase={updatedCase}
              glassSurface={glassSurface}
            />
          </Box>
          <Box sx={{ mb: "10px" }}>
            <TabsSection
              caseId={caseId}
              lawyerData={lawyerData}
              dataCase={dataCase}
              glassSurface={glassSurface}
              // onFinish={() => console.log("terminado!")}
            />
          </Box>
          <Box sx={{ mb: "10px" }}>
            <FlowSectionAccordion
              setFlag={setFlag}
              flag={flag}
              dataCase={dataCase}
              caseContract={caseContract}
              acceptedContract={acceptedContract}
              caseId={caseId}
              userLogged={userLogged}
              mailTemplates={mailTemplates}
              updatedCase={updatedCase}
              glassSurface={glassSurface}
              setUpdatedCase={setUpdatedCase}
              contactConditionIfExist={contactConditionIfExist}
              requireContractAssignment={requireContractAssignment}
              contractsList={contractsList}
              newContract={newContract}
              contractInputValue={contractInputValue}
              setNewContract={setNewContract}
              setContractInputValue={setContractInputValue}
              handleConfirmNewContract={handleConfirmNewContract}
            />
          </Box>
        </Box>
      </Fade>
    </>
  );

  return (
    <div className="fatherContainer">
      <CommonSubHeader title="Expedientes" subTitle="Ver" />

      {!hasPermission && <Unauthorized />}

      {flag === false && (
        <Grid container item xs={12} md={11} m={"auto"}>
          <Grid item xs={12} sx={{ marginBottom: "15px" }}>
            <Skeleton
              width={"100%"}
              height={370}
              style={{ transform: "none" }}
            />
          </Grid>
          <Grid item xs={12} sx={{ marginBottom: "15px" }}>
            <Skeleton
              width={"100%"}
              height={270}
              style={{ transform: "none" }}
            />
          </Grid>
          <Grid item xs={12} sx={{ marginBottom: "15px" }}>
            <Skeleton
              width={"100%"}
              height={470}
              style={{ transform: "none" }}
            />
          </Grid>
        </Grid>
      )}

      {hasPermission && content}
    </div>
  );
};

export default ViewCase;
